import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { changeUac, uacQuery } from '../api/devices'
import { toast } from '../ui/notify'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'

/**
 * UAC Settings - opteemum/devices/details/userAccountSettings.php (si fila
 * "UAC Settings" din Remote Control > Security). Glisorul vertical al
 * machetei era bootstrap-slider (0 = Never notify, 3 = Always notify); aici e
 * un `<input type="range">` pe verticala, cu aceleasi patru texte in dreapta
 * si acelasi alert deasupra. La eliberarea glisorului se posteaza la
 * `/devices/{id}/uac` (Opteemum::changeUacSettings: `UAC <nivel>` + eveniment),
 * cu acelasi toast. Pe Linux / macOS nu exista: portalul nu randa nimic.
 */

const UAC_CSS = `
.od-uac-slider { display: flex; flex-direction: column; align-items: center; }
.od-uac-slider input[type=range] { writing-mode: vertical-lr; direction: rtl; height: 210px; width: 24px; margin: 6px 0; cursor: pointer; }
.od-uac-info { border: 1px solid #ddd; min-height: 260px; padding: 20px; position: relative; }
.od-uac-info ul { margin: 20px; }
.od-uac-info ul li { list-style-type: disc; padding: 5px; }
.od-uac-note { position: absolute; bottom: 15px; }
`

export function DeviceUac({ id, ticket }: ScreenProps) {
  const { data, error } = useQuery(uacQuery(id))
  const queryClient = useQueryClient()
  const [shown, setShown] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />
  if (!data.supported) return null

  const level = shown ?? data.uac
  const info = data.levels[level] ?? data.levels[2]

  const save = async (next: number) => {
    if (busy || next === data.uac) return
    setBusy(true)
    try {
      const res = await changeUac(id, next, ticket ?? 0)
      if (!res.ok) {
        toast(res.message ?? 'Something went wrong.', 'Error', 'error')
        return
      }
      toast('Change UAC Settings instruction sent.', 'Success')
      await queryClient.invalidateQueries({ queryKey: ['device', id, 'uac'] })
    } catch {
      toast('Something went wrong.', 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <style>{UAC_CSS}</style>
      <div className="alert alert-info">
        <i className="fas fa-info-circle" aria-hidden="true"></i> User Account Control helps prevent potentially harmful programs from
        making changes to your computer.
      </div>

      <div className="row">
        <div className="col-md-2">
          <div className="od-uac-slider">
            <div style={{ textAlign: 'center', marginBottom: 10 }}>
              <b>Always notify</b>
            </div>
            <input
              id="uacSettings"
              type="range"
              min={0}
              max={3}
              step={1}
              value={level}
              disabled={busy}
              data-device={id}
              aria-label="UAC level"
              onChange={(e) => setShown(Number(e.target.value))}
              onMouseUp={(e) => void save(Number((e.target as HTMLInputElement).value))}
              onKeyUp={(e) => void save(Number((e.target as HTMLInputElement).value))}
              onTouchEnd={(e) => void save(Number((e.target as HTMLInputElement).value))}
            />
            <div style={{ textAlign: 'center', marginTop: 10 }}>
              <b>Never notify</b>
            </div>
          </div>
        </div>
        <div className="col-md-10">
          <div className="od-uac-info">
            <b>{info.title}</b>
            <ul>
              {info.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <div className="od-uac-note">
              <i className="fas fa-info-circle color-blue font-14" aria-hidden="true"></i> {info.note}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
